import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { setCurrentView } from "../../actions/viewActions";
import vpgLogo from '../../assets/images/vpg-health-logo.png';
import flexcoaLogo from '../../assets/images/flexcoa-logo-condensed-green.png';
import flexcoaLogoWhite from '../../assets/images/flexcoa-logo-condensed.png';
import {
  ArrowRight, ArrowUpRight, BookOpen, CalendarClock, ClipboardCheck, FileText, LayoutDashboard,
  LayoutTemplate, MessagesSquare, ShieldCheck, Smartphone, UserCheck, Users, Building2,
} from 'lucide-react';

// Landing page for signed-out visitors (FECOA-2174).

const LEARN_MORE_URL = 'https://vpghealth.com/ecoa/';

const capabilities = [
  {
    title: 'Assessment Builder',
    description: 'Build questionnaires screen by screen, with scoring and branching rules, and send them for review before they go live.',
    icon: LayoutTemplate,
  },
  {
    title: 'Study schedules',
    description: 'Set visits, assessment windows and reminders once. Every participant follows the same schedule.',
    icon: CalendarClock,
  },
  {
    title: 'Participants and caregivers',
    description: 'Participants and caregivers answer on their own phone or in clinic, even without a connection.',
    icon: Smartphone,
  },
  {
    title: 'Compliance and reporting',
    description: 'See completion by site and participant, and follow up on missed assessments.',
    icon: ClipboardCheck,
  },
  {
    title: 'Data clarification',
    description: 'Raise, track and resolve data queries with site teams in one place.',
    icon: MessagesSquare,
  },
  {
    title: 'Access and security',
    description: 'Multi-factor sign-in, role-based access and a history of changes to studies and assessments.',
    icon: ShieldCheck,
  },
];

// How a question looks to a participant on a phone (a numeric rating scale).
const PhoneQuestion = () => (
  <div className="mx-auto w-[17.5rem] rounded-[2.25rem] border border-gray-300 bg-white p-2 shadow-sm dark:border-gray-600 dark:bg-gray-800" aria-hidden="true">
    <div className="rounded-[1.75rem] border border-gray-100 bg-white px-5 pb-6 pt-5 dark:border-gray-700 dark:bg-gray-900">
      <div className="mx-auto mb-5 h-1 w-14 rounded-full bg-gray-200 dark:bg-gray-700" />
      <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-700 dark:text-gray-300">Daily diary</span>
        <span>Question 2 of 6</span>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div className="h-full w-1/3 rounded-full bg-primary-600" />
      </div>

      <p className="mt-7 text-[15px] font-medium leading-snug text-gray-900 dark:text-gray-50">
        How would you rate your pain right now?
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">0 means no pain, 10 means the worst pain you can imagine.</p>

      <div className="mt-6 space-y-1.5">
        {[[0, 1, 2, 3, 4, 5], [6, 7, 8, 9, 10]].map((row) => (
          <div key={row[0]} className="flex justify-center gap-1.5">
            {row.map((n) => (
              <span
                key={n}
                className={`flex h-9 w-9 items-center justify-center rounded-md border text-sm font-medium tabular-nums ${
                  n === 4
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300'
                }`}
              >
                {n}
              </span>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-gray-500 dark:text-gray-400">
        <span>No pain</span>
        <span>Worst pain</span>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-2">
        <span className="flex h-10 items-center justify-center rounded-md border border-gray-200 text-sm font-medium text-gray-700 dark:border-gray-700 dark:text-gray-300">
          Back
        </span>
        <span className="flex h-10 items-center justify-center rounded-md bg-primary-600 text-sm font-medium text-white">
          Next
        </span>
      </div>
    </div>
  </div>
);

// A small, static picture of the staff workspace (sample data, not real).
const PREVIEW_SITES = [
  { code: '101', name: 'Northside Clinic', inUse: 49, total: 52 },
  { code: '204', name: 'Lakeview Research', inUse: 38, total: 41 },
  { code: '310', name: 'Riverside Medical', inUse: 32, total: 35 },
];

const WorkspacePreview = () => (
  <div
    className="rounded-2xl border border-gray-200/80 bg-canvas p-2 shadow-overlay dark:border-white/10"
    aria-hidden="true"
  >
    <div className="flex gap-2">
      <div className="hidden w-36 flex-shrink-0 px-1.5 pt-2.5 sm:block">
        <img src={flexcoaLogo} alt="" className="h-5 w-auto dark:hidden" />
        <img src={flexcoaLogoWhite} alt="" className="hidden h-5 w-auto dark:block" />
        <div className="mt-5 space-y-0.5 text-[11px]">
          {[
            [LayoutDashboard, 'Studies', true],
            [BookOpen, 'Study Builder', false],
            [FileText, 'Assessment Builder', false],
          ].map(([Icon, label, active]) => (
            <div
              key={label}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${
                active
                  ? 'bg-white font-semibold text-gray-900 shadow-xs ring-1 ring-gray-200/70 dark:bg-white/[0.07] dark:text-white dark:ring-white/10'
                  : 'font-medium text-gray-500 dark:text-gray-400'
              }`}
            >
              <Icon className={`h-3 w-3 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} strokeWidth={2} />
              {label}
            </div>
          ))}
        </div>
      </div>

      <div className="min-w-0 flex-1 rounded-xl border border-gray-200/80 bg-white p-4 dark:border-white/[0.07] dark:bg-gray-900">
        <div className="flex items-center gap-2">
          <p className="truncate text-[13px] font-semibold text-gray-900 dark:text-gray-50">Remote Pain Study</p>
          <span className="badge badge-dot badge-info px-1.5 py-0 text-[10px]">In Progress</span>
        </div>
        <p className="mt-0.5 font-mono text-[10px] text-gray-500 dark:text-gray-400">RPS-2026</p>

        <div className="mt-3 flex gap-4 border-b border-gray-200 text-[10.5px] font-medium dark:border-white/10">
          <span className="-mb-px border-b-2 border-primary-600 pb-1.5 text-gray-900 dark:border-primary-400 dark:text-white">Overview</span>
          <span className="pb-1.5 text-gray-500 dark:text-gray-400">Sites</span>
          <span className="pb-1.5 text-gray-500 dark:text-gray-400">DCQs</span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          {[
            [Users, 'Participants', '128', null],
            [UserCheck, 'In use', '119', 93],
            [Building2, 'Sites', '3', null],
          ].map(([Icon, label, value, pct]) => (
            <div key={label} className="rounded-lg border border-gray-200/80 p-2.5 dark:border-white/[0.07]">
              <div className="flex items-center justify-between text-[10px] font-medium text-gray-500 dark:text-gray-400">
                {label}
                <Icon className="h-3 w-3 text-gray-400" strokeWidth={2} />
              </div>
              <p className="mt-1 text-base font-semibold tabular-nums text-gray-900 dark:text-gray-50">{value}</p>
              {pct ? (
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.08]">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${pct}%` }} />
                </div>
              ) : (
                <div className="mt-1.5 h-1" />
              )}
            </div>
          ))}
        </div>

        <div className="mt-3 overflow-hidden rounded-lg border border-gray-200/80 dark:border-white/[0.07]">
          <div className="bg-gray-50/70 px-2.5 py-1.5 text-[9.5px] font-medium text-gray-500 dark:bg-white/[0.02] dark:text-gray-400">Sites</div>
          {PREVIEW_SITES.map((site) => (
            <div key={site.code} className="flex items-center gap-2 border-t border-gray-100 px-2.5 py-2 text-[10.5px] dark:border-white/[0.06]">
              <span className="rounded bg-gray-100 px-1 font-mono text-[9px] text-gray-600 dark:bg-white/[0.06] dark:text-gray-300">{site.code}</span>
              <span className="min-w-0 flex-1 truncate font-medium text-gray-800 dark:text-gray-200">{site.name}</span>
              <span className="hidden h-1 w-14 overflow-hidden rounded-full bg-gray-100 sm:block dark:bg-white/[0.08]">
                <span className="block h-full rounded-full bg-primary-500" style={{ width: `${Math.round((site.inUse / site.total) * 100)}%` }} />
              </span>
              <span className="tabular-nums text-gray-500 dark:text-gray-400">
                {site.inUse}/{site.total}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Home = ({ setCurrentView }) => {
  useEffect(() => {
    setCurrentView("home");
  }, [setCurrentView]);

  return (
    <div>
      {/* Introduction */}
      <section className="overflow-hidden border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-14 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16 lg:px-8 lg:pb-28 lg:pt-20">
          <div>
            <img
              src={vpgLogo}
              alt="VPG Health"
              className="h-12 w-auto dark:brightness-0 dark:invert"
            />

            <h1 className="mt-9 max-w-xl text-4xl font-semibold leading-[1.1] tracking-tight text-gray-900 sm:text-5xl dark:text-gray-50">
              Clinical outcome assessments, captured on any device
            </h1>

            <p className="mt-6 max-w-lg text-lg leading-relaxed text-gray-600 dark:text-gray-300">
              flexCOA is the electronic clinical outcome assessment (eCOA) platform from VPG Health.
              Study teams build questionnaires and visit schedules, and participants and caregivers
              complete them on a phone, a site tablet or the web.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/login" className="btn btn-primary btn-lg">
                Sign in
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={LEARN_MORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-lg">
                Learn More
              </a>
            </div>
          </div>

          <div className="relative lg:pl-6">
            <WorkspacePreview />
            <div className="absolute -bottom-20 hidden origin-bottom-left scale-[0.72] md:-left-4 md:block lg:-left-12">
              <PhoneQuestion />
            </div>
          </div>
        </div>
      </section>

      {/* What it covers */}
      <section className="bg-canvas dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-[28px] dark:text-gray-50">
              What study teams use flexCOA for
            </h2>
            <p className="mt-3 text-base leading-relaxed text-gray-600 dark:text-gray-400">
              Designing assessments, running them across sites and reviewing the data, in one place.
            </p>
          </div>

          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(({ title, description, icon: Icon }) => (
              <li key={title} className="card p-6">
                <h3 className="flex items-center gap-2 text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                  <Icon className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" strokeWidth={2} aria-hidden="true" />
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* VPG Health */}
      <section className="border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-16 sm:px-6 lg:grid-cols-[18rem_minmax(0,1fr)] lg:gap-16 lg:px-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">Built by VPG Health</h2>
          <div className="max-w-2xl">
            <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
              VPG Health brings together qualitative research, data analysis and software development.
              flexCOA puts that experience into a platform for capturing clinical outcome data.
            </p>
            <a
              href={LEARN_MORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="link mt-5 inline-flex items-center gap-1 text-sm"
            >
              Explore flexCOA™ at vpghealth.com
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-8 text-sm text-gray-500 sm:flex-row sm:px-6 lg:px-8 dark:text-gray-400">
          <img src={flexcoaLogo} alt="flexCOA" className="h-7 w-auto dark:hidden" />
          <img src={flexcoaLogoWhite} alt="" aria-hidden="true" className="hidden h-7 w-auto dark:block" />
          <span>© {new Date().getFullYear()} VPG Health. flexCOA™ is a VPG Health product.</span>
        </div>
      </footer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentView: state.views.currentView,
});

const mapDispatchToProps = {
  setCurrentView,
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
